"use client";

import Filters from "@/components/Filters";
import InstanceTable from "@/components/InstanceTable";
import { useInstanceData } from "@/state";
import { HetznerInstance } from "@/utils/colunnData/hetzner";
import dynamicallyDecompress from "@/utils/dynamicallyDecompress";
import { useMemo } from "react";
import { MarketingSchema, hasBanner } from "@/schemas/marketing";
import Advert from "@/components/Advert";
import type { CurrencyItem } from "@/utils/loadCurrencies";

type Props = {
    instanceCount: number;
    regions: {
        [key: string]: string;
    };
    compressedInstances: [string[], ...HetznerInstance[]];
    marketingData: MarketingSchema;
    currencies: CurrencyItem[];
};

export default function HetznerClient({
    instanceCount,
    regions,
    compressedInstances,
    marketingData,
    currencies,
}: Props) {
    const initialInstances = useMemo(() => {
        const rainbowTable = compressedInstances.shift() as string[];
        if (!Array.isArray(rainbowTable)) {
            // This is probably dev.
            compressedInstances.unshift(rainbowTable);
            return compressedInstances as HetznerInstance[];
        }
        return compressedInstances.map((instance) =>
            // @ts-expect-error: This is wrong, but close enough to work.
            dynamicallyDecompress(instance, rainbowTable),
        );
    }, [compressedInstances]);

    // All server types ship in the initial payload, so there is no lazy pipeline.
    const allInstances = useInstanceData<HetznerInstance>(
        null,
        initialInstances as HetznerInstance[],
    );

    const full = !hasBanner(marketingData)
        ? "h-[calc(100vh-6em)]"
        : "h-[calc(100vh-8.5em)]";

    return (
        <>
            <Advert
                marketingData={marketingData}
                instanceGroup="hetzner-home"
                gpu={false}
            />
            <main className={`${full} overflow-y-hidden flex flex-col`}>
                <Filters
                    currencies={currencies}
                    columnAtomKey="hetzner"
                    regions={{
                        local_zone: {},
                        main: regions,
                        wavelength: {},
                        china: {},
                    }}
                    reservedTermOptions={[]}
                    hideEcu={true}
                />
                <div className="flex-1 min-h-0">
                    <InstanceTable
                        instances={allInstances}
                        instanceCount={instanceCount}
                        columnAtomKey="hetzner"
                    />
                </div>
            </main>
        </>
    );
}
